import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Search, MessageSquare, CreditCard, LayoutGrid, Rows3, ShieldAlert, ReceiptText, ArrowUpRight, X } from "lucide-react";
import { api, fmtUSD } from "@/lib/api";
import { PersonAvatar } from "@/components/PersonAvatar";

const FlagBadges = ({ e }) => (
  <div className="flex flex-wrap items-center gap-1">
    {e.violations > 0 && (
      <span className="inline-flex items-center gap-1 rounded-md bg-[rgba(239,68,68,0.14)] px-2 py-0.5 text-[10.5px] font-medium text-[#F87171]">
        <ShieldAlert size={10} /> {e.violations}
      </span>
    )}
    {e.pending_receipts > 0 && (
      <span className="inline-flex items-center gap-1 rounded-md bg-[rgba(245,158,11,0.14)] px-2 py-0.5 text-[10.5px] font-medium text-[#FBBF24]">
        <ReceiptText size={10} /> {e.pending_receipts}
      </span>
    )}
    {e.violations === 0 && e.pending_receipts === 0 && <span className="text-[11px] text-[#4ADE80]">Clean</span>}
  </div>
);

export const EmployeeDirectory = () => {
  const [employees, setEmployees] = useState(null);
  const [search, setSearch] = useState("");
  const [view, setView] = useState(() => localStorage.getItem("emp_view") || "table");
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const flag = searchParams.get("flag");
  const spendSort = searchParams.get("sort") === "spend";

  useEffect(() => {
    api.get("/employees").then((r) => setEmployees(r.data)).catch(() => {});
  }, []);

  const setViewPersist = (v) => { setView(v); localStorage.setItem("emp_view", v); };

  let filtered = employees?.filter((e) =>
    !search || e.name.toLowerCase().includes(search.toLowerCase()) || e.department.toLowerCase().includes(search.toLowerCase())
  );
  if (filtered && flag === "violations") filtered = filtered.filter((e) => e.violations > 0);
  if (filtered && flag === "receipts") filtered = filtered.filter((e) => e.pending_receipts > 0);
  if (filtered && spendSort) filtered = [...filtered].sort((a, b) => (b.monthly_spend || 0) - (a.monthly_spend || 0));

  const ariaFilterLabel = flag === "violations" ? "Has open violations"
    : flag === "receipts" ? "Owes receipts"
    : spendSort ? "Ranked by spend" : null;

  return (
    <div className="space-y-4" data-testid="employees-page">
      <div className="flex flex-wrap items-center gap-3">
        <div>
          <h2 className="text-[15px] font-semibold tracking-tight">Employee directory</h2>
          <p className="text-[11.5px] text-[#A3A3A3]">{employees ? `${employees.length} people · click anyone to open their Aria thread` : "Loading…"}</p>
        </div>

        <div className="flex w-full flex-wrap items-center gap-2 sm:ml-auto sm:w-auto">
          {ariaFilterLabel && (
            <span data-testid="employees-aria-filter-chip"
              className="flex items-center gap-1.5 rounded-md bg-[rgba(79,142,247,0.14)] py-1 pl-2.5 pr-1.5 text-[11.5px] text-[#cfe0fb]">
              Aria filter: {ariaFilterLabel}
              <button onClick={() => setSearchParams({})} data-testid="employees-aria-filter-clear"
                className="flex h-4 w-4 items-center justify-center rounded text-[#6b6b6b] hover:bg-[rgba(255,255,255,0.1)] hover:text-white">
                <X size={10} />
              </button>
            </span>
          )}
          <div className="relative min-w-0 flex-1 sm:flex-none">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#555]" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search people or departments…"
              data-testid="employees-search-input"
              className="h-8 w-full border-[#222] bg-[#111] pl-8 text-[12.5px] text-white placeholder:text-[#555] sm:w-60" />
          </div>
          {/* view toggle */}
          <div className="flex items-center rounded-lg border border-[#222] bg-[#111] p-0.5" data-testid="employees-view-toggle">
            <button onClick={() => setViewPersist("table")} data-testid="employees-view-table"
              className={`flex h-7 items-center gap-1.5 rounded-md px-2.5 text-[11.5px] transition-colors ${view === "table" ? "bg-[#1c1c1c] text-white" : "text-[#6b6b6b] hover:text-white"}`}>
              <Rows3 size={13} /> Table
            </button>
            <button onClick={() => setViewPersist("grid")} data-testid="employees-view-grid"
              className={`flex h-7 items-center gap-1.5 rounded-md px-2.5 text-[11.5px] transition-colors ${view === "grid" ? "bg-[#1c1c1c] text-white" : "text-[#6b6b6b] hover:text-white"}`}>
              <LayoutGrid size={13} /> Grid
            </button>
          </div>
        </div>
      </div>

      {/* TABLE VIEW */}
      {view === "table" && (
        <div className="overflow-hidden rounded-[14px] border border-[#222] bg-[#111111] card-shadow">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px] text-left" data-testid="employees-table">
              <thead>
                <tr className="border-b border-[#1e1e1e]">
                  {["Employee", "Department", "Manager", "Corporate card", "Flags", "Spend (period)", "Aria chats", ""].map((h) => (
                    <th key={h} className="px-4 py-2.5 text-[10.5px] font-semibold uppercase tracking-wider text-[#6b6b6b]">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {!filtered ? [...Array(8)].map((_, i) => (
                  <tr key={i} className="border-b border-[#181818]"><td colSpan={8} className="px-4 py-3"><Skeleton className="h-5 w-full bg-[#1a1a1a]" /></td></tr>
                )) : filtered.map((e) => (
                  <tr key={e.id} onClick={() => navigate(`/employees/${e.id}`)}
                    data-aria-key={`employee:${e.id}`} data-aria-label={e.name}
                    className="cursor-pointer border-b border-[#181818] hover:bg-[#161616]" data-testid={`employee-row-${e.id}`}>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-2.5">
                        <PersonAvatar name={e.name} size={32} color={e.avatar_color} />
                        <div>
                          <div className="text-[13px] font-medium text-white">{e.name}</div>
                          <div className="text-[10.5px] text-[#6b6b6b]">{e.role}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-[12px] text-[#A3A3A3]">{e.department}</td>
                    <td className="px-4 py-2.5 text-[12px] text-[#A3A3A3]">{e.manager}</td>
                    <td className="px-4 py-2.5">
                      <span className="flex items-center gap-1.5 text-[12px] tabular-nums text-[#c9c9c9]">
                        <CreditCard size={12} className="text-[#555]" /> •••• {e.card_last4}
                      </span>
                    </td>
                    <td className="px-4 py-2.5"><FlagBadges e={e} /></td>
                    <td className="px-4 py-2.5 text-[13px] font-medium tabular-nums text-white">{fmtUSD(e.monthly_spend)}</td>
                    <td className="px-4 py-2.5">
                      <span className="flex items-center gap-1.5 text-[12px] tabular-nums text-[#A3A3A3]">
                        <MessageSquare size={12} className={e.open_conversations > 0 ? "text-[#4F8EF7]" : "text-[#555]"} />
                        {e.open_conversations || 0} open
                      </span>
                    </td>
                    <td className="px-4 py-2.5">
                      <ArrowUpRight size={14} className="text-[#555]" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* GRID VIEW */}
      {view === "grid" && (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3" data-testid="employees-grid">
          {!filtered ? [...Array(9)].map((_, i) => <Skeleton key={i} className="h-40 rounded-[14px] bg-[#141414]" />) : filtered.map((e) => (
            <motion.button key={e.id} whileHover={{ y: -2 }} transition={{ type: "spring", stiffness: 420, damping: 28 }}
              onClick={() => navigate(`/employees/${e.id}`)} data-testid={`employee-card-${e.id}`}
              data-aria-key={`employee:${e.id}`} data-aria-label={e.name}
              className="card-lift rounded-[14px] border border-[#222] bg-[#111111] p-4 text-left card-shadow">
              <div className="flex items-center gap-3">
                <PersonAvatar name={e.name} size={44} color={e.avatar_color} />
                <div className="min-w-0">
                  <div className="truncate text-[13.5px] font-medium text-white">{e.name}</div>
                  <div className="truncate text-[11px] text-[#6b6b6b]">{e.role} · {e.department}</div>
                </div>
              </div>

              <div className="mt-3 flex items-center justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-[#6b6b6b]">Spend</div>
                  <div className="text-[15px] font-semibold tabular-nums text-white">{fmtUSD(e.monthly_spend)}</div>
                </div>
                <FlagBadges e={e} />
              </div>

              <div className="mt-3 flex items-center justify-between border-t border-[#1a1a1a] pt-2.5 text-[11px] text-[#A3A3A3]">
                <span className="flex items-center gap-1.5">
                  <MessageSquare size={12} className={e.open_conversations > 0 ? "text-[#4F8EF7]" : "text-[#555]"} />
                  {e.open_conversations || 0} open chats
                </span>
                <span className="flex items-center gap-1 text-[#7FAEF9]">Open thread <ArrowUpRight size={11} /></span>
              </div>
            </motion.button>
          ))}
        </div>
      )}
    </div>
  );
};
